import { api } from './client';
import type {
  Session,
  SessionDetail,
  SeatMap,
  Order,
  Ticket,
  Cinema,
  Sector,
  Banner,
  AdminOrder,
  PaymentCard,
} from '../types/models';

// ---------- PUBLIC ----------
export const fetchSessions = () => api.get<Session[]>('/api/afisha').then((r) => r.data);
export const fetchSession = (id: number | string) => api.get<SessionDetail>(`/api/sessions/${id}`).then((r) => r.data);
export const fetchSeatMap = (id: number | string) => api.get<SeatMap>(`/api/sessions/${id}/seatmap`).then((r) => r.data);
export const fetchBanners = () => api.get<Banner[]>('/api/banners').then((r) => r.data);

export const holdSeat = (sessionId: number, seatId: number) =>
  api
    .post<{
      ok: boolean;
      order_id: number;
      held_seats: number[];
      total_amount: number;
      hold_ttl: number;
    }>('/api/booking/hold', { session_id: sessionId, seat_id: seatId })
    .then((r) => {
      const heldUntil = new Date(Date.now() + (r.data.hold_ttl || 0) * 1000).toISOString();
      return { ...r.data, held_until: heldUntil };
    });

export const releaseSeat = (sessionId: number, seatId: number) =>
  api.post('/api/booking/release', { session_id: sessionId, seat_id: seatId }).then((r) => r.data);

export const checkoutSeats = (sessionId: number, seatIds: number[]) =>
  api
    .post<{
      ok: boolean;
      order_id: number;
      held_seats: number[];
      total_amount: number;
      hold_ttl: number;
    }>('/api/booking/checkout', { session_id: sessionId, seat_ids: seatIds })
    .then((r) => r.data);

export const fetchOrder = (orderId: number | string) =>
  api.get<Order>(`/api/me/orders/${orderId}`).then((r) => r.data);

export const payOrder = (orderId: number) =>
  api.post('/api/booking/i_paid', { order_id: orderId }).then((r) => r.data);

export const cancelOrder = (orderId: number) =>
  api.post(`/api/booking/cancel/${orderId}`).then((r) => r.data);

export const fetchActiveOrder = () =>
  api.get<Order | null>('/api/me/active-order').then((r) => r.data);

export const fetchMyTickets = () => api.get<Ticket[]>('/api/me/tickets').then((r) => r.data);

// ---------- PUBLIC: cinemas ----------
export const fetchCinema = (id: number | string) =>
  api.get<Cinema>(`/api/cinemas/${id}`).then((r) => r.data);

// ---------- ADMIN ----------
export const adminCinemas = () => api.get<Cinema[]>('/api/admin/cinemas').then((r) => r.data);
export const adminCreateCinema = (payload: Partial<Cinema>) =>
  api.post<Cinema>('/api/admin/cinemas', payload).then((r) => r.data);
export const adminUpdateCinema = (id: number, payload: Partial<Cinema>) =>
  api.patch<Cinema>(`/api/admin/cinemas/${id}`, payload).then((r) => r.data);
export const adminDeleteCinema = (id: number) =>
  api.delete(`/api/admin/cinemas/${id}`).then((r) => r.data);
export const adminCinemaSectors = (cinemaId: number | string) =>
  api.get<Sector[]>(`/api/admin/cinemas/${cinemaId}/sectors`).then((r) => r.data);
export const adminSaveSectors = (cinemaId: number | string, sectors: Sector[]) =>
  api.put(`/api/admin/cinemas/${cinemaId}/sectors`, { sectors }).then((r) => r.data);

export const adminSessions = (archive = false) =>
  api.get<any[]>(`/api/admin/sessions${archive ? '?archive=true' : ''}`).then((r) => r.data);
export const adminCreateSession = (payload: any) =>
  api.post('/api/admin/sessions', payload).then((r) => r.data);
export const adminUpdateSession = (id: number, payload: any) =>
  api.patch(`/api/admin/sessions/${id}`, payload).then((r) => r.data);
export const adminDeleteSession = (id: number) =>
  api.delete(`/api/admin/sessions/${id}`).then((r) => r.data);

export const adminUploadPhoto = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api
    .post<{ file_id: string; url: string }>('/api/admin/upload/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    })
    .then((r) => r.data);
};

export const adminOrders = () => api.get<AdminOrder[]>('/api/admin/orders').then((r) => r.data);
export const adminApproveOrder = (id: number) =>
  api.post(`/api/admin/orders/${id}/approve`).then((r) => r.data);
export const adminRejectOrder = (id: number, reason: string) =>
  api.post(`/api/admin/orders/${id}/reject`, { reason }).then((r) => r.data);
export const adminRefundOrder = (id: number) =>
  api.post(`/api/admin/orders/${id}/refund`).then((r) => r.data);

// admin tickets list = list of paid orders with their tickets (per-order view)
export const adminTickets = (filters: Record<string, any> = {}) =>
  api.get('/api/admin/orders', { params: filters }).then((r) => r.data);

export const adminScan = (payload: string) =>
  api.post('/api/admin/scan', { payload }).then((r) => r.data);

export const adminBroadcastCreate = (payload: any) =>
  api.post('/api/admin/broadcasts', payload).then((r) => r.data);
export const adminBroadcastSend = (id: number) =>
  api.post(`/api/admin/broadcasts/${id}/send`).then((r) => r.data);
export const adminBroadcasts = () =>
  api.get('/api/admin/broadcasts').then((r) => r.data);

export const adminBanners = () => api.get<Banner[]>('/api/admin/banners').then((r) => r.data);
export const adminSaveBanner = (payload: Partial<Banner>) =>
  api.post<Banner>('/api/admin/banners', payload).then((r) => r.data);
export const adminDeleteBanner = (id: number) =>
  api.delete(`/api/admin/banners/${id}`).then((r) => r.data);

export const adminPaymentCards = () =>
  api.get<PaymentCard[]>('/api/admin/payment-cards').then((r) => r.data);
export const adminCreatePaymentCard = (payload: Partial<PaymentCard>) =>
  api.post<PaymentCard>('/api/admin/payment-cards', payload).then((r) => r.data);
export const adminUpdatePaymentCard = (id: number, payload: Partial<PaymentCard>) =>
  api.patch<PaymentCard>(`/api/admin/payment-cards/${id}`, payload).then((r) => r.data);
export const adminDeletePaymentCard = (id: number) =>
  api.delete(`/api/admin/payment-cards/${id}`).then((r) => r.data);
